// Resolving one release: pointer, descriptor, two artifacts, four verifications.
//
// The classification here is the design's load-bearing asymmetry. Availability
// failures (offline, unavailable, forbidden) fall back to the cached release;
// integrity failures do not. Serving
// an older release after a digest mismatch would hide a bad promotion, which is
// the class of bug that reaches learners and is hardest to diagnose — the exact
// opposite of what a cache is for.

import { ManifestAssetSource } from "@aybuben/core/content/assets.ts";
import {
	contentBundleDigest,
	audioManifestDigest,
	parseCourseBundle,
	parseAudioManifest,
	ContentFormatError,
	type CourseBundle,
	type AudioManifest,
} from "@aybuben/core/content/bundle.ts";
import {
	parsePointer,
	parseReleaseDescriptor,
	type ReleaseDescriptor,
} from "@aybuben/core/content/release.ts";
import {
	loadedContent,
	ContentError,
	type ContentSource,
	type LoadedContent,
} from "@aybuben/core/content/source.ts";
import type { ContentTransport } from "@aybuben/core/content/transport.ts";
import { contentVersionOf } from "@aybuben/core/content/version.ts";

import { codeOf, type FilesApi, type RowsApi } from "../appwrite/sdk.ts";
import { revalidateCached, type ReleaseCache } from "./release-cache.ts";

// Exported only for appwrite.test.ts, the same reason auth/appwrite.ts exports authCode.
export const classify = (e: unknown): ContentError => {
	if (e instanceof ContentError) return e;
	// A bundle or manifest we could not parse is an integrity failure, never an
	// availability one. It has no `.code`, so without this branch it would fall
	// through to the "no code means the network" case below and the loader would
	// serve a stale release over a corrupt one — hiding a bad promotion behind a
	// working fallback, which is the failure this whole asymmetry exists to catch.
	if (e instanceof ContentFormatError)
		return new ContentError("integrity", `malformed content: ${e.message}`);
	const code = codeOf(e);
	const message = e instanceof Error ? e.message : String(e);
	// No code at all is the network: fetch rejected before a status existed. The React Native SDK
	// wraps that rejection as an exception whose code defaults to 0.
	// It wraps any non-Appwrite error in call() the same way (malformed JSON, say), so those read as
	// offline too; accepted, since the loader then falls back to the cached release.
	if (code === undefined || code === 0) return new ContentError("offline", message);
	if (code === 401) return new ContentError("unauthorized", `appwrite ${code}`);
	// A permissions or platform-configuration problem. The session is intact, so
	// this must not reach Root's sign-out branch, which reads "unauthorized".
	if (code === 403) return new ContentError("forbidden", `appwrite ${code}`);
	if (code >= 500) return new ContentError("offline", `appwrite ${code}`);
	// 404 and every other 4xx: the release this build was pointed at is not
	// there or not readable. Not an availability problem — there is nothing to
	// fall back *to* that would be correct, and a stale release would be a lie.
	return new ContentError("integrity", `appwrite ${code}`);
};

export interface TransportSdk {
	tables: RowsApi;
	storage: FilesApi;
	/** Fetches a file URL with the session: the web sends its cookie, native its header. */
	fetchFile: (url: string) => Promise<Response>;
	databaseId: string;
	tableId: string;
	bucketId: string;
	currentRow: string;
}

export class AppwriteTransport implements ContentTransport {
	constructor(private readonly sdk: TransportSdk) {}

	async pointer(): Promise<string> {
		const row = await this.sdk.tables.getRow({
			databaseId: this.sdk.databaseId,
			tableId: this.sdk.tableId,
			rowId: this.sdk.currentRow,
		});
		return String((row as { document?: unknown }).document);
	}

	async descriptor(releaseId: string): Promise<string> {
		const row = await this.sdk.tables.getRow({
			databaseId: this.sdk.databaseId,
			tableId: this.sdk.tableId,
			rowId: releaseId,
		});
		return String((row as { document?: unknown }).document);
	}

	async fileBytes(fileId: string): Promise<Uint8Array> {
		// The SDK returns a URL string; the response body is the fetch, which the
		// platform supplies because it decides how the session rides along.
		const response = await this.sdk.fetchFile(
			this.sdk.storage.getFileDownload({ bucketId: this.sdk.bucketId, fileId }),
		);
		if (!response.ok)
			throw Object.assign(new Error(`download ${response.status}`), { code: response.status });
		return new Uint8Array(await response.arrayBuffer());
	}

	downloadUrl(fileId: string): string {
		return this.sdk.storage.getFileDownload({ bucketId: this.sdk.bucketId, fileId });
	}
}

interface Resolved {
	readonly content: LoadedContent;
	readonly descriptor: ReleaseDescriptor;
	readonly courseJson: string;
	readonly audioManifestJson: string;
}

function assertAudioResolves(bundle: CourseBundle, manifest: AudioManifest): void {
	for (const item of bundle.data.items) {
		const name = (item as { audio?: unknown }).audio;
		if (name === undefined) continue;
		if (typeof name !== "string")
			throw new ContentError(
				"integrity",
				`item ${(item as { id: string }).id} has a non-string audio field`,
			);
		if (!manifest.assets[name]) {
			throw new ContentError(
				"integrity",
				`course references audio "${name}", which the manifest does not contain`,
			);
		}
	}
}

export class AppwriteContentSource implements ContentSource {
	private resolved: Promise<LoadedContent> | null = null;

	constructor(
		private readonly transport: ContentTransport,
		private readonly cache: ReleaseCache,
	) {}

	load(): Promise<LoadedContent> {
		// The memo pins a *successful* resolution only. Caching the rejected promise
		// would make every later call replay the same failure, so a learner who lost
		// connectivity for one launch could never recover in that session.
		this.resolved ??= this.resolve().catch((e: unknown) => {
			this.resolved = null;
			throw e;
		});
		return this.resolved;
	}

	private async resolve(): Promise<LoadedContent> {
		let r: Resolved;
		try {
			r = await this.fromRemote();
		} catch (e) {
			const err = classify(e);
			// Only availability failures fall back. An integrity failure means the
			// published release is broken, and serving an older one would conceal it.
			// A 403 counts as availability: the release is fine, this learner just
			// cannot reach it right now. An unauthorized session still throws.
			if (err.kind !== "offline" && err.kind !== "unavailable" && err.kind !== "forbidden")
				throw err;
			const cached = await this.cache.read();
			if (!cached) throw err;
			// read() returns a record only once revalidateCached has accepted it.
			const { bundle, manifest } = (await revalidateCached(cached))!;
			return loadedContent(
				cached.descriptor.releaseId,
				bundle.contentVersion,
				bundle.data,
				{ ...bundle.locales },
				new ManifestAssetSource(manifest, (id) => this.transport.downloadUrl(id)),
			);
		}
		// After verification, never before: a cache write that happened first
		// could persist a release this method is about to reject. Awaited, because
		// `ReleaseCache.write` absorbs store failures — and a fire-and-forget write that loses its race
		// with app teardown costs the learner their next offline launch.
		await this.cache.write({
			descriptor: r.descriptor,
			courseJson: r.courseJson,
			audioManifestJson: r.audioManifestJson,
			cachedAt: Date.now(),
		});
		return r.content;
	}

	private async fromRemote(): Promise<Resolved> {
		const pointer = parsePointer(await this.transport.pointer());
		const descriptor = parseReleaseDescriptor(await this.transport.descriptor(pointer.targetId));
		if (descriptor.releaseId !== pointer.targetId) {
			throw new ContentError("integrity", "pointer and descriptor disagree on the release id");
		}
		const courseBytes = await this.transport.fileBytes(descriptor.course.fileId);
		if ((await contentBundleDigest(courseBytes)) !== descriptor.course.sha256) {
			throw new ContentError("integrity", `course ${descriptor.course.fileId} digest mismatch`);
		}
		const manifestBytes = await this.transport.fileBytes(descriptor.audioManifest.fileId);
		if ((await audioManifestDigest(manifestBytes)) !== descriptor.audioManifest.sha256) {
			throw new ContentError(
				"integrity",
				`audio manifest ${descriptor.audioManifest.fileId} digest mismatch`,
			);
		}
		const courseJson = new TextDecoder().decode(courseBytes);
		const audioManifestJson = new TextDecoder().decode(manifestBytes);
		const bundle = parseCourseBundle(courseJson);
		const manifest = parseAudioManifest(audioManifestJson);
		if (bundle.contentVersion !== descriptor.course.contentVersion) {
			throw new ContentError("integrity", "bundle and descriptor disagree on contentVersion");
		}
		if (contentVersionOf(bundle.data) !== bundle.contentVersion) {
			throw new ContentError("integrity", "bundle contentVersion does not match its own data");
		}
		assertAudioResolves(bundle, manifest);
		const assets = new ManifestAssetSource(manifest, (id) => this.transport.downloadUrl(id));
		return {
			content: loadedContent(
				descriptor.releaseId,
				bundle.contentVersion,
				bundle.data,
				{ ...bundle.locales },
				assets,
			),
			descriptor,
			courseJson,
			audioManifestJson,
		};
	}
}
